import React, { useEffect, useState } from "react";
import Page from 'components/page';
import { Button, Col, Form, FormInstance, Input, Row, Select, Typography } from "antd";
import { useNavigate, useParams } from 'react-router-dom';
import withAuthorization from "../../components/hocs/withAuthorization";
import { CitiesByStateByCountry, Countries, EXCLUDED_COUNTRIES, RealmRoles, StatesByCountry } from "../../constants";
import useCardPhysicalConversion from "../../hooks/useCardPhysicalConversion";
import { CountryCode, IAddress } from "../../interfaces/card-holder";
import useGetCardHolder from "../../hooks/useGetCardHolder";
import useToastContext from "../../hooks/useToastContext";
import { ICardEmboss, IEmboss, IFulfillment, IShipping } from "../../interfaces/card-emboss";

type NewCardFormInputs = {
  cardName: string;
  trackingNumber: string;
  fourthLine: string;
  distributionCode: string;
  city: string;
  state: string;
  country: CountryCode;
  postalCode: string;
  comments: string;
  streetName: string;
  numberAddress: string;
  departmentName: string;
  departmentNumber: string;
}

function getAddressByType(addresses: IAddress[]){
  const add = addresses?.find(ad => { return ad.type === 'LEGAL' } );
  return add !== undefined ? add : addresses !== undefined ? addresses[0] : null;
}

function getCardNameWithSize19(fullCardName: String) {
  return fullCardName?.length > 19 ? fullCardName?.slice(0,19) : fullCardName ;
}

const CardStateNew = () => {
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const { id, issuer } = useParams<{ id: string; issuer: string }>();
  const country = Form.useWatch('country', form);
  const state = Form.useWatch('state', form);
  const { cardHolder} = useGetCardHolder(localStorage?.getItem('chId'));
  const address = getAddressByType(cardHolder?.addresses);
  const [isLoading, setIsLoading] = useState(false)
  const toast = useToastContext();
  const {
    createCardPhysical,
    isError: validateIsError
  } = useCardPhysicalConversion(id);

  useEffect(() => {
    form.setFieldValue('cardName', getCardNameWithSize19(cardHolder?.first_name?.concat(" ".concat(cardHolder?.last_name))));
    form.setFieldValue('country', address?.country);
    form.setFieldValue('state', address?.state);
    form.setFieldValue('city', address?.city);
    form.setFieldValue('postalCode', address?.postal_code);
    form.setFieldValue('numberAddress', address?.street_number);
    form.setFieldValue('streetName', address?.street);
    form.setFieldValue('departmentName', address?.apt);
    form.setFieldValue('departmentNumber', address?.floor);
  }, [cardHolder]);

  const onSubmit = async () => {
    const shipping: IShipping = {
      street: form.getFieldValue('streetName'),
      street_number: form.getFieldValue('numberAddress'),
      city: form.getFieldValue('city'),
      state: form.getFieldValue('state'),
      country: form.getFieldValue('country'),
      postal_code: form.getFieldValue('postalCode'),
      floor: form.getFieldValue('departmentNumber'),
      apt: form.getFieldValue('departmentName'),
      extra_comment: form.getFieldValue('comments')
    }
    const fulfillment: IFulfillment = {
      code_currier: form.getFieldValue('distributionCode'),
      shipping: shipping,
      tracking_id: form.getFieldValue('trackingNumber')
    }
    const emboss: IEmboss = {
      fourth_line: form.getFieldValue('fourthLine'),
      name: form.getFieldValue('cardName')
    }

    const payload: ICardEmboss = {
      emboss: emboss,
      fulfillment: fulfillment
    }

    try {
      setIsLoading(true);
      const createCardPhysicalResult = await createCardPhysical(payload);

      setIsLoading(false);
      if(createCardPhysicalResult.status === 204) {
        toast.success('Exito', 'Solicitud de tarjeta realizada correctamente. Redireccionando...')
        setInterval(
          () => navigate(`/${issuer}/cards/${id}`),
          5000
        );
      } else {
        throw validateIsError;
      }
    } catch (e: unknown) {
      setIsLoading(false);
      toast.error('Error', 'Ocurrió un error al realizar la solicitud.');
      console.error(e);
    }
  }

  const SubmitButton = ({ form, loading }: { form: FormInstance; loading: boolean }) => {
    const [submittable, setSubmittable] = React.useState(false);
    const values = Form.useWatch([], form);
    React.useEffect(() => {
      form.validateFields({ validateOnly: true }).then(
        () => {
          setSubmittable(true);
        },
        () => {
          setSubmittable(false);
        }
      );
    }, [values]);

    return (
      <Button type="primary" htmlType="submit" disabled={!submittable} loading={loading}>
        Actualizar estado
      </Button>
    );
  }

  return (
    <Page title="Cambiar estado de tarjeta">
      <Form form={form} layout="vertical" autoComplete="off" onFinish={onSubmit}>
        <Row>
          <Col span={12}>
            <Typography.Title level={4}>Personalización</Typography.Title>
          </Col>
          <Col span={12}>
            <Typography.Title level={4}>Dirección de envío</Typography.Title>
          </Col>
        </Row>
        <Row gutter={16}>
          <Col span={8}>
            <Form.Item<NewCardFormInputs>
              name="cardName"
              label="Nombre de la tarjeta"
              rules={[{ required: true, min: 1, max: 19 }]}
            >
              <Input />
            </Form.Item>
          </Col>
          <Col span={8} offset={4}>
            <Form.Item<NewCardFormInputs>
              name="trackingNumber"
              label="Número de seguimiento"
              rules={[{ required: true, min: 1, max: 250 }]}
            >
              <Input />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item<NewCardFormInputs>
              name="fourthLine"
              label="Cuarta linea"
              rules={[{ required: false, max: 19 }]}
            >
              <Input />
            </Form.Item>
          </Col>
          <Col span={8} offset={4}>
            <Form.Item<NewCardFormInputs>
              name="distributionCode"
              label="Código de correo de distribución"
              rules={[{ required: true, min: 1, max: 2 }]}
            >
              <Input />
            </Form.Item>
          </Col>
          <Col span={5} offset={12}>
            <Form.Item<NewCardFormInputs>
              name="streetName"
              label="Calle"
              rules={[{ required: true, min: 1, max: 255 }]}
            >
              <Input />
            </Form.Item>
          </Col>
          <Col span={3} >
            <Form.Item<NewCardFormInputs>
              name="numberAddress"
              label="Número"
              rules={[{ required: true, min: 1, max: 250 }]}
            >
              <Input />
            </Form.Item>
          </Col>
          <Col span={4} offset={12}>
            <Form.Item<NewCardFormInputs>
              name="departmentName"
              label="Departamento"
              rules={[{ required: false, min: 1, max: 3 }]}
            >
              <Input />
            </Form.Item>
          </Col>
          <Col span={4} >
            <Form.Item<NewCardFormInputs>
              name="departmentNumber"
              label="Número"
              rules={[{ required: false, min: 1 }]}
            >
              <Input />
            </Form.Item>
          </Col>
          <Col span={8} offset={12}>
            <Form.Item<NewCardFormInputs>
              name="country"
              label="Pais"
              rules={[{ required: true, message: 'Por favor ingresar pais.', min: 1, max: 3 }]}
            >
              <Select
                placeholder="Seleccionar pais"
                showSearch
                options={Object.entries(Countries)
                  .filter(([code]) => !EXCLUDED_COUNTRIES.includes(code))
                  .map(([code, name]) => ({ label: name, value: code }))}
              />
            </Form.Item>
          </Col>
          <Col span={8} offset={12}>
            <Form.Item<NewCardFormInputs>
              name="state"
              label="Estado"
              rules={[{ required: true, message: 'Por favor ingresar estado.', min: 1, max: 5 }]}
            >
              <Select
                placeholder="Seleccionar estado"
                showSearch
                options={Object.entries(StatesByCountry?.[country] ?? {}).map(([stateCode, name]) => ({
                  label: name,
                  value: stateCode,
                }))}
              />
            </Form.Item>
          </Col>
          <Col span={8} offset={12}>
            <Form.Item<NewCardFormInputs>
              name="city"
              label="Ciudad"
              rules={[{ required: true, message: 'Por favor ingresar ciudad.', min: 1, max: 40 }]}
            >
              <Select
                placeholder="Seleccionar ciudad"
                showSearch
                options={CitiesByStateByCountry?.[country]?.[state]?.toSorted()?.map((name) => ({
                  label: name,
                  value: name,
                }))}
              />
            </Form.Item>
          </Col>
          <Col span={8} offset={12}>
            <Form.Item<NewCardFormInputs>
              name="postalCode"
              label="Codigo Postal"
              rules={[{ required: true, message: 'Ingresar codigo postal.', min: 1, max: 10 }]}
            >
              <Input />
            </Form.Item>
          </Col>
          <Col span={8} offset={12}>
            <Form.Item<NewCardFormInputs>
              name="comments"
              label="Comentarios extras"
              rules={[{ required: false, max: 255 }]}
            >
              <Input.TextArea allowClear />
            </Form.Item>
          </Col>
        </Row>
        <Row>
          <Col span={24} style={{ textAlign: 'center' }}>
            <SubmitButton form={form} loading={isLoading} />
          </Col>
        </Row>
      </Form>
    </Page>
  );
};

export default withAuthorization([RealmRoles.CardsAllWrite], CardStateNew);